// ============================================================
// HomeFAQ.jsx
//
// Full-page FAQ with a sidebar for smooth-scroll navigation
// between four topic categories:
//   - General
//   - Training
//   - Platform Features
//   - Cloud & DevOps
//
// Clean Code:
//   - All FAQ data extracted into FAQ_SECTIONS constant
//   - Refs generated dynamically from section data
//   - Brand colors centralized in BRAND constant
//   - No repeated JSX — everything rendered via .map()
// ============================================================

import * as React from "react";
import { useRef } from "react";
import { styled } from "@mui/material/styles";
import {
    Accordion, AccordionSummary, AccordionDetails,
    Typography, Box, Chip,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────────
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WbCloudyIcon from "@mui/icons-material/WbCloudy";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import LanIcon from "@mui/icons-material/Lan";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import BuildIcon from "@mui/icons-material/Build";
import GroupsIcon from "@mui/icons-material/Groups";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DevicesIcon from "@mui/icons-material/Devices";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

// ============================================================
//  CONSTANTS
// ============================================================

/**
 * Brand colors — consistent with the rest of the platform.
 */
const BRAND = {
    dark:   "#100720",
    gold:   "#E5C95F",
    white:  "#FFFFFF",
    muted:  "#475569",
    text:   "#1e293b",
    border: "#e5e5e5",
    bg:     "#f5f5f5",
};

/** Background image for the page wrapper */
const BG_IMAGE = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/**
 * FAQ_SECTIONS
 * All FAQ content organized into four categories.
 * Each section has:
 *   - id       : unique key used for scroll ref lookup
 *   - label    : displayed in sidebar and as section title
 *   - icon     : MUI icon shown in sidebar and section header
 *   - questions: array of { icon, question, answer }
 *
 * To add a new section or question, update this array only.
 */
const FAQ_SECTIONS = [
    {
        id:    "general",
        label: "General",
        icon:  <ManageAccountsIcon fontSize="small" />,
        questions: [
            {
                icon:     <CodeIcon fontSize="small" />,
                question: "What technologies does ProDev specialize in?",
                answer:   "We specialize in React, Node.js, Angular, Spring Boot, Java, Python, TypeScript, C#, C++, Express.js, MongoDB, MySQL, Postgres, GitHub, Linux, Embedded IoT, Software Testing, and HTML5 & CSS — 16 professional course tracks in total.",
            },
            {
                icon:     <EmojiEventsIcon fontSize="small" />,
                question: "What makes ProDev different from other platforms?",
                answer:   "We combine an inbuilt online compiler (no local setup needed), hands-on coding assignments with solutions, extensive DSA training, goal setting, online exams, and professional certification — all in one platform. Premium members also get production project development and live deployment guidance.",
            },
            {
                icon:     <GroupsIcon fontSize="small" />,
                question: "Who is this platform designed for?",
                answer:   "ProDev is built for aspiring software developers, career switchers, and working professionals who want industry-standard skills. Whether you're a complete beginner or an experienced developer looking to advance, our structured course tracks cover every level.",
            },
        ],
    },
    {
        id:    "training",
        label: "Training",
        icon:  <LocalLibraryIcon fontSize="small" />,
        questions: [
            {
                icon:     <SchoolIcon fontSize="small" />,
                question: "Do you offer one-on-one mentorship?",
                answer:   "Yes — personalized mentorship is at the core of our training model. Standard and Premium plan users get access to online coding support from instructors. Premium Individual users additionally receive guided production project development.",
            },
            {
                icon:     <DevicesIcon fontSize="small" />,
                question: "Is the learning flexible?",
                answer:   "Absolutely. Learn at your own pace using our structured course tracks. Set personal goals and deadlines on your User Dashboard, and track your progress. The platform is designed for both students and working professionals.",
            },
            {
                icon:     <EmojiEventsIcon fontSize="small" />,
                question: "How does certification work?",
                answer:   "Upon completing your course curriculum, assignments, and online exams, you are awarded a professional certificate through the platform. Certification is available on all plans — Basic, Standard, and Premium Individual.",
            },
            {
                icon:     <BuildIcon fontSize="small" />,
                question: "What is included in the DSA training?",
                answer:   "Every plan includes extensive Data Structures and Algorithms training covering arrays, linked lists, trees, graphs, sorting algorithms, recursion, dynamic programming, and more — designed to build your coding muscle for technical interviews and real-world projects.",
            },
        ],
    },
    {
        id:    "platform",
        label: "Platform Features",
        icon:  <LanIcon fontSize="small" />,
        questions: [
            {
                icon:     <CodeIcon fontSize="small" />,
                question: "Do I need to install anything to start coding?",
                answer:   "No. All plans include our inbuilt online compiler. You can write, run, and test code directly in your browser across all 16 course tracks — no local installation required.",
            },
            {
                icon:     <RocketLaunchIcon fontSize="small" />,
                question: "Can I build and deploy full-stack apps?",
                answer:   "Yes. Our platform supports complete full-stack development workflows. Premium Individual plan members get hands-on production project development and deployment to live environments as part of their plan.",
            },
            {
                icon:     <GroupsIcon fontSize="small" />,
                question: "Do you support collaboration tools?",
                answer:   "Yes — we cover GitHub workflows, pull requests, CI/CD pipelines, and GitHub Actions as part of the GitHub course track. Students also work with industry tools like Jira, Miro, and KanbanFlow in project modules.",
            },
            {
                icon:     <SupportAgentIcon fontSize="small" />,
                question: "What support is available if I get stuck?",
                answer:   "Basic plan users have access to course materials and detailed assignment solutions. Standard plan users get online coding support from instructors. Premium users get full instructor support plus guided project development.",
            },
        ],
    },
    {
        id:    "cloud",
        label: "Cloud & DevOps",
        icon:  <WbCloudyIcon fontSize="small" />,
        questions: [
            {
                icon:     <WbCloudyIcon fontSize="small" />,
                question: "Do you offer cloud training?",
                answer:   "Yes — our DevOps & Tools track covers cloud infrastructure including AWS, Google Cloud, Docker, CI/CD pipelines, and GitHub Actions. Linux system administration is also a dedicated course track.",
            },
            {
                icon:     <RocketLaunchIcon fontSize="small" />,
                question: "Is deployment covered in the courses?",
                answer:   "Yes. DevOps concepts including containerisation with Docker, cloud deployment on AWS and Google Cloud, and automated CI/CD pipelines are covered. Premium Individual members additionally get hands-on live deployment on real production projects.",
            },
            {
                icon:     <GroupsIcon fontSize="small" />,
                question: "Are instructors experienced professionals?",
                answer:   "All instructors are industry professionals with real-world software engineering and cloud experience. They bring practical knowledge from building and deploying production systems, not just academic theory.",
            },
        ],
    },
];

// ============================================================
//  STYLED COMPONENTS
// ============================================================

/**
 * PageWrapper
 * Full-page background image container with dark overlay.
 */
const PageWrapper = styled("div")({
    backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${BG_IMAGE})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    minHeight: "100vh",
    padding: "60px 20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    fontFamily: "'Poppins', sans-serif",
});

/**
 * Layout
 * Horizontal two-column layout: sidebar + content.
 * Stacks vertically on screens below 900px.
 */
const Layout = styled("div")({
    display: "flex",
    width: "100%",
    maxWidth: "1200px",
    gap: "30px",
    "@media (max-width: 900px)": {
        flexDirection: "column",
        alignItems: "center",
    },
});

/**
 * Sidebar
 * Sticky left panel with section navigation links.
 */
const Sidebar = styled("div")({
    width: "240px",
    backgroundColor: BRAND.white,
    borderRadius: "16px",
    padding: "20px",
    border: `1px solid ${BRAND.border}`,
    height: "fit-content",
    position: "sticky",
    top: "40px",
    "@media (max-width: 900px)": {
        width: "100%",
        position: "relative",
        top: 0,
    },
});

/**
 * SidebarItem
 * Clickable navigation link in the sidebar.
 * Highlights with a gold left border on hover.
 */
const SidebarItem = styled("div")(({ active }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 14px",
    fontWeight: 600,
    fontSize: "0.9rem",
    color: active ? BRAND.dark : BRAND.muted,
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    borderLeft: active
        ? `3px solid ${BRAND.gold}`
        : "3px solid transparent",
    backgroundColor: active ? "rgba(229,201,95,0.08)" : "transparent",
    "&:hover": {
        backgroundColor: BRAND.bg,
        color: BRAND.dark,
        borderLeft: `3px solid ${BRAND.gold}`,
    },
}));

/**
 * Content
 * Main content area with glassmorphism-style white background.
 */
const Content = styled("div")({
    flex: 1,
    backgroundColor: "rgba(255,255,255,0.96)",
    padding: "36px",
    borderRadius: "16px",
    border: `1px solid ${BRAND.border}`,
    backdropFilter: "blur(6px)",
});

/**
 * StyledAccordion
 * Clean card-style accordion with lift-on-hover effect.
 * Gold left border when expanded.
 */
const StyledAccordion = styled(Accordion)({
    backgroundColor: BRAND.white,
    borderRadius: "12px !important",
    marginBottom: "12px",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "none",
    transition: "transform 0.25s ease, border-color 0.2s ease",
    "&:before": { display: "none" },
    "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
    },
    "&.Mui-expanded": {
        borderLeft: `4px solid ${BRAND.gold}`,
    },
});

/**
 * StyledAccordionSummary
 * Gold expand icon, dark on hover for clear interactivity.
 */
const StyledAccordionSummary = styled(AccordionSummary)({
    px: 3,
    "& .MuiAccordionSummary-expandIconWrapper": {
        color: BRAND.gold,
    },
    "&:hover": {
        backgroundColor: BRAND.bg,
        "& .MuiAccordionSummary-expandIconWrapper": {
            color: BRAND.dark,
        },
    },
});

// ============================================================
//  MAIN COMPONENT
// ============================================================

export default function HomeFAQ() {

    // ── Refs ─────────────────────────────────────────────────
    // One ref per FAQ section for smooth scroll navigation.
    // We use plain { current: null } objects — React.createRef()
    // inside useRef does NOT attach to DOM nodes correctly.
    const sectionRefs = useRef(
        FAQ_SECTIONS.reduce((acc, section) => {
            acc[section.id] = { current: null };
            return acc;
        }, {})
    );

    // ── Active sidebar item ───────────────────────────────────
    const [activeSection, setActiveSection] = React.useState(FAQ_SECTIONS[0].id);

    // ============================================================
    //  HANDLERS
    // ============================================================

    /**
     * handleScrollTo
     * Scrolls smoothly to the target section and
     * marks it as active in the sidebar.
     *
     * @param {string} sectionId - The id of the section to scroll to
     */
    const handleScrollTo = (sectionId) => {
        sectionRefs.current[sectionId]?.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
        setActiveSection(sectionId);
    };

    // ============================================================
    //  RENDER
    // ============================================================

    return (
        <PageWrapper>
            <Layout>

                {/* ── Sidebar Navigation ── */}
                <Sidebar>
                    {/* Sidebar header */}
                    <Typography
                        variant="overline"
                        sx={{
                            fontSize: "0.7rem",
                            fontWeight: 800,
                            color: BRAND.muted,
                            letterSpacing: "2px",
                            display: "block",
                            mb: 1.5,
                            px: 1,
                        }}
                    >
                        CATEGORIES
                    </Typography>

                    {/* Navigation links — rendered from FAQ_SECTIONS */}
                    {FAQ_SECTIONS.map((section) => (
                        <SidebarItem
                            key={section.id}
                            active={activeSection === section.id ? 1 : 0}
                            onClick={() => handleScrollTo(section.id)}
                        >
                            <Box sx={{
                                color: activeSection === section.id
                                    ? BRAND.gold
                                    : BRAND.muted,
                                display: "flex",
                            }}>
                                {section.icon}
                            </Box>
                            {section.label}
                        </SidebarItem>
                    ))}

                    {/* Total question count */}
                    <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${BRAND.border}` }}>
                        <Chip
                            label={`${FAQ_SECTIONS.reduce((n, s) => n + s.questions.length, 0)} Questions`}
                            size="small"
                            sx={{
                                backgroundColor: BRAND.dark,
                                color: BRAND.gold,
                                fontWeight: 700,
                                fontSize: "0.72rem",
                                width: "100%",
                            }}
                        />
                    </Box>
                </Sidebar>

                {/* ── FAQ Content ── */}
                <Content>
                    {FAQ_SECTIONS.map((section, sectionIndex) => (
                        <Box
                            key={section.id}
                            ref={sectionRefs.current[section.id]}
                            sx={{ mt: sectionIndex === 0 ? 0 : 6 }}
                        >
                            {/* Section heading */}
                            <Box
                                display="flex" alignItems="center" gap={1.5}
                                sx={{
                                    mb: 3, pb: 2,
                                    borderBottom: `2px solid ${BRAND.border}`,
                                    borderLeft: `5px solid ${BRAND.gold}`,
                                    pl: 2,
                                }}
                            >
                                <Box sx={{ color: BRAND.dark, display: "flex" }}>
                                    {section.icon}
                                </Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    color={BRAND.dark}
                                    sx={{ fontFamily: "'Georgia', serif" }}
                                >
                                    {section.label}
                                </Typography>
                                <Chip
                                    label={`${section.questions.length}`}
                                    size="small"
                                    sx={{
                                        backgroundColor: "rgba(229,201,95,0.15)",
                                        color: BRAND.dark,
                                        fontWeight: 800,
                                        fontSize: "0.7rem",
                                        border: `1px solid ${BRAND.gold}`,
                                    }}
                                />
                            </Box>

                            {/* Accordion questions */}
                            {section.questions.map((item, qIndex) => (
                                <StyledAccordion key={qIndex}>
                                    <StyledAccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls={`${section.id}-q${qIndex}-content`}
                                        id={`${section.id}-q${qIndex}-header`}
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            {/* Question icon */}
                                            <Box sx={{ color: BRAND.gold, display: "flex" }}>
                                                {item.icon}
                                            </Box>
                                            <Typography
                                                fontWeight={700}
                                                color={BRAND.text}
                                                fontSize="0.95rem"
                                            >
                                                {item.question}
                                            </Typography>
                                        </Box>
                                    </StyledAccordionSummary>

                                    <AccordionDetails sx={{ pl: 6 }}>
                                        <Typography
                                            sx={{
                                                color: BRAND.muted,
                                                lineHeight: 1.8,
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            {item.answer}
                                        </Typography>
                                    </AccordionDetails>
                                </StyledAccordion>
                            ))}
                        </Box>
                    ))}
                </Content>

            </Layout>
        </PageWrapper>
    );
}
